// ytdModuleCompletion.js - Modal content for YTD Module Completion KPI
// Place at: src/components/modalContent/productionMilestonesModalComponents/ytdModuleCompletion.js

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import useYtdModuleCompletionData from "../../../hooks/useYtdModuleCompletionData";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../../modalContent/SortableTableHeader";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#cc0000" }}
        />
        Completed: {d.cumCompleted ?? "-"}
      </div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#1a1a1a" }}
        />
        Target: {d.cumTarget}
      </div>
    </div>
  );
};

const YtdModuleCompletion = ({ goals }) => {
  const { metrics, months, loading, error } = useYtdModuleCompletionData();

  const target = goals?.ytdCompletion || 95.0;

  // ── Derived display values ────────────────────────────────────────────────
  const {
    ytdPct,
    deltaVsTarget,
    monthlyAvg,
    bestMonth,
    bestMonthCount,
    missedMonths,
  } = metrics;

  const aheadBehindLabel =
    deltaVsTarget >= 0
      ? `+${deltaVsTarget} ahead of YTD target`
      : `${Math.abs(deltaVsTarget)} behind YTD target`;

  // ── Add delta column to monthly data for table ────────────────────────────
  const monthlyDataWithDelta = months.map((d) => ({
    ...d,
    delta: d.completed != null ? d.completed - d.target : null,
  }));

  // ── Table sorting ─────────────────────────────────────────────────────────
  // Month name to number mapping for chronological sorting
  const MONTH_ORDER = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const { sortedData, requestSort, getSortClass } = useTableSort(
    monthlyDataWithDelta,
    { initialKey: "month", initialDirection: "asc" },
    {
      month: (a, b, direction) => {
        const ai = MONTH_ORDER.findIndex((m) => a.month?.startsWith(m));
        const bi = MONTH_ORDER.findIndex((m) => b.month?.startsWith(m));
        const order = (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        return direction === "desc" ? -order : order;
      },
    },
  );
  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pml-modal-flex-content pml-modal-loading">
        Loading YTD module completion data…
      </div>
    );
  }

  if (error) {
    return (
      <div className="pml-modal-flex-content pml-modal-error">{error}</div>
    );
  }

  return (
    <div className="pml-modal-flex-content">
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--4">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">YTD Completion</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${ytdPct >= target ? "ok" : "alert"}`}
          >
            {ytdPct}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">vs Target</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${deltaVsTarget >= 0 ? "ok" : "alert"}`}
          >
            {aheadBehindLabel.split(" ")[0]}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Monthly Avg</span>
          <span className="pml-modal-stat-value">{monthlyAvg}</span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Months Missed</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${missedMonths === 0 ? "ok" : "alert"}`}
          >
            {missedMonths}
          </span>
        </div>
      </div>

      {/* Line chart - cumulative completed vs target */}
      <p className="pml-modal-sub-title">Cumulative Completion vs Target</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={months}
            margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Dashed black target line */}
            <Line
              type="monotone"
              dataKey="cumTarget"
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={false}
              name="Target"
            />
            {/* Red actual line - connectNulls=false stops line at last known month */}
            <Line
              type="monotone"
              dataKey="cumCompleted"
              stroke="#cc0000"
              strokeWidth={2}
              dot={{ r: 4, fill: "#cc0000", strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              connectNulls={false}
              name="Completed"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly breakdown table */}
      <p className="pml-modal-sub-title">Monthly Breakdown</p>
      <div className="pml-modal-scroll-wrap pml-table-auto-height">
        <table className="pml-modal-table">
          <thead>
            <tr>
              <SortableTableHeader
                columnKey="month"
                onSort={requestSort}
                sortClass={getSortClass("month")}
              >
                Month
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="target"
                onSort={requestSort}
                sortClass={getSortClass("target")}
                style={{ textAlign: "center" }}
              >
                Target
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="completed"
                onSort={requestSort}
                sortClass={getSortClass("completed")}
                style={{ textAlign: "center" }}
              >
                Completed
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="delta"
                onSort={requestSort}
                sortClass={getSortClass("delta")}
                style={{ textAlign: "center" }}
              >
                Delta
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="cumCompleted"
                onSort={requestSort}
                sortClass={getSortClass("cumCompleted")}
                style={{ textAlign: "center" }}
              >
                Cum. Completed
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="cumTarget"
                onSort={requestSort}
                sortClass={getSortClass("cumTarget")}
                style={{ textAlign: "center" }}
              >
                Cum. Target
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((d) => {
              const isFuture = d.completed == null;
              return (
                <tr key={d.month}>
                  <td style={{ fontWeight: 600 }}>{d.month}</td>
                  <td style={{ textAlign: "center" }}>{d.target}</td>
                  <td style={{ textAlign: "center", fontWeight: 700 }}>
                    {isFuture ? "-" : d.completed}
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      fontWeight: 700,
                      color: isFuture
                        ? "#a0aec0"
                        : d.delta >= 0
                          ? "#38a169"
                          : "#cc0000",
                    }}
                  >
                    {isFuture ? "-" : d.delta >= 0 ? `+${d.delta}` : d.delta}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {d.cumCompleted ?? "-"}
                  </td>
                  <td style={{ textAlign: "center", color: "#718096" }}>
                    {d.cumTarget}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer - best month */}
      {bestMonth && (
        <div style={{ fontSize: 12, color: "#a0aec0" }}>
          Best month: {bestMonth} ({bestMonthCount} modules completed)
        </div>
      )}
    </div>
  );
};

export default YtdModuleCompletion;
